// Block D (2026-10-08): the CRM pipeline's numbers and colours, worked out in one place so the
// page, the list and the tests all agree.
//
// Deal value and next step live on the card: CrmContactBoard.customFields.deal_value (a number, the
// key the New Business template already uses for its "Deal value" column) and .next_step (text).
// A stage is "open" until it is terminal. Terminal stages hold both won and lost deals (the New
// Business template has Won AND Lost as terminal), and a stage has no outcome column yet, so a
// terminal stage counts as lost only when its name says so (Lost, Lapsed, Rejected...). That is
// the D-b item "stage outcome".
import { TOKENS } from '../../ui/tokens';
import { formatMoney } from '../../utils/currency';

export const DEAL_VALUE_KEY = 'deal_value';
export const NEXT_STEP_KEY = 'next_step';
export const UNASSIGNED_ID = '__unassigned__';
export const SIGNED_HIDE_MS = 60 * 24 * 60 * 60 * 1000;

// Board tabs and stages without a usable colour take the palette by position (no stored colour).
export const BOARD_PALETTE = ['pastel-blue', 'pastel-pink', 'pastel-mint', 'pastel-yellow', 'pastel-lilac', 'pastel-orange', 'pastel-periwinkle'];
const STAGE_PALETTE = ['pastel-periwinkle', 'pastel-blue', 'pastel-yellow', 'pastel-orange', 'pastel-green', 'pastel-pink', 'pastel-lilac'];
export const boardColor = (index) => BOARD_PALETTE[((index % BOARD_PALETTE.length) + BOARD_PALETTE.length) % BOARD_PALETTE.length];

function hexToHsl(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 255) / 255; const g = ((n >> 8) & 255) / 255; const b = (n & 255) / 255;
  const max = Math.max(r, g, b); const min = Math.min(r, g, b); const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0));
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return { h: h * 60, s, l };
}

// The pastel tokens a stage colour can land on (mint is left out: it and green share a hue).
const HUE_TARGETS = ['pastel-coral', 'pastel-orange', 'pastel-yellow', 'pastel-green', 'pastel-blue', 'pastel-periwinkle', 'pastel-lilac', 'pastel-pink']
  .map((token) => ({ token, h: hexToHsl(TOKENS[token]).h }));

// A stage's stored colour is a legacy hex (#6366f1). Drawn through the nearest pastel token by hue,
// so the board stays on the palette; a grey (low saturation) is neutral; none takes its position.
export function stageColor(stage, index = 0) {
  const hsl = hexToHsl(stage?.colour);
  if (!hsl) return STAGE_PALETTE[index % STAGE_PALETTE.length];
  if (hsl.s < 0.2) return 'pastel-neutral';
  let best = HUE_TARGETS[0]; let bestD = 999;
  for (const t of HUE_TARGETS) {
    const d = Math.min(Math.abs(t.h - hsl.h), 360 - Math.abs(t.h - hsl.h));
    if (d < bestD) { best = t; bestD = d; }
  }
  return best.token;
}

// Decision 80: a stage's outcome (open | won | lost) is set on the stage, not read from its name.
// The name rule below is only the fallback for a stage that arrives without one (the migration
// backfilled every existing stage from exactly this rule, so the two agree on old data).
const LOST_NAME = /\b(lost|lapsed|reject|rejected|declined|cancel+ed|dead|no.?go|churned)\b/i;
const OUTCOMES = new Set(['open', 'won', 'lost']);
export function stageOutcome(stage) {
  if (OUTCOMES.has(stage?.outcome)) return stage.outcome;
  if (!stage?.isTerminal) return 'open';
  return LOST_NAME.test(stage?.name || '') ? 'lost' : 'won';
}
export const isLostStage = (stage) => stageOutcome(stage) === 'lost';
export const isWonStage = (stage) => stageOutcome(stage) === 'won';
export const isOpenStage = (stage) => stageOutcome(stage) === 'open';

// A card's deal value as a number, or null when there is none (an empty field is not £0).
export function dealValue(card) {
  const raw = card?.boardEntry?.customFields?.[DEAL_VALUE_KEY];
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(String(raw).replace(/[£,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
}
export const nextStep = (card) => {
  const v = card?.boardEntry?.customFields?.[NEXT_STEP_KEY];
  return typeof v === 'string' && v.trim() ? v.trim() : '';
};

// The board's currency: the deal value column may name one (options.currency); otherwise £.
// One board, one currency, so totals never add different currencies (decision 72).
export function boardCurrency(columns) {
  const col = (columns || []).find((c) => c.fieldKey === DEAL_VALUE_KEY);
  const opts = col?.options;
  const code = opts && typeof opts === 'object' && !Array.isArray(opts) ? opts.currency : null;
  return typeof code === 'string' && /^[A-Z]{3}$/.test(code) ? code : 'GBP';
}

// Whole units for tiles and pills ("£1,200"); formatMoney's pence are noise on a board.
export function money(n, code = 'GBP') {
  const full = formatMoney(Math.round(n || 0), code);
  return full.replace(/\.00$/, '');
}

export const isSignedLongAgo = (c, now = Date.now()) =>
  !!c?.contractSignedAt && now - new Date(c.contractSignedAt).getTime() > SIGNED_HIDE_MS;

// Every number on the page from the stages as the server sent them (stages: [{ ...stage,
// contacts }], unassigned cards in their own lane) and the follow-up groups by contact id.
export function pipelineStats(stages, followUps = {}) {
  let openValue = 0; let openCount = 0; let wonCount = 0; let valued = 0; let follow = 0; let total = 0;
  for (const stage of stages) {
    const open = stage.id === UNASSIGNED_ID || isOpenStage(stage);
    for (const card of stage.contacts || []) {
      total += 1;
      if (followUps[card.id] && !isLostStage(stage)) follow += 1;   // a lost deal is not chased (decision 80)
      if (open) {
        openCount += 1;
        const v = dealValue(card);
        if (v !== null) { openValue += v; valued += 1; }
      } else if (isWonStage(stage)) wonCount += 1;
    }
  }
  const decided = wonCount + openCount;
  return {
    total, openCount, openValue, valuedOpen: valued, wonCount, followCount: follow,
    // Only a number when there is something to divide; never "0%" from nothing.
    winRate: decided > 0 && wonCount > 0 ? Math.round((wonCount / decided) * 100) : null,
  };
}

export function stageTotal(stage) {
  let sum = 0; let any = false;
  for (const c of stage.contacts || []) { const v = dealValue(c); if (v !== null) { sum += v; any = true; } }
  return any ? sum : null;
}

// "Today", "1d ago", "12d ago", or null when never contacted.
export function daysAgo(dt, now = Date.now()) {
  if (!dt) return null;
  const t = new Date(dt).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.floor((now - t) / 86400000));
}
export function lastContactLabel(dt, flagged, now = Date.now()) {
  const d = daysAgo(dt, now);
  const base = d === null ? 'Never' : d === 0 ? 'Today' : `${d}d`;
  if (flagged) return `${base} · follow up`;
  return d === null ? 'Never contacted' : d === 0 ? 'Today' : `${d}d ago`;
}

// Move one card to a stage in a copy of the stages (optimistic moves and their undo).
export function moveCard(stages, contactId, toStageId) {
  const card = stages.flatMap((s) => s.contacts || []).find((c) => c.id === contactId);
  if (!card) return stages;
  const moved = { ...card, boardEntry: { ...(card.boardEntry || {}), stageId: toStageId === UNASSIGNED_ID ? null : toStageId } };
  return stages.map((s) => ({
    ...s,
    contacts: s.id === toStageId
      ? [...(s.contacts || []).filter((c) => c.id !== contactId), moved]
      : (s.contacts || []).filter((c) => c.id !== contactId),
  }));
}

export const stageOfCard = (stages, contactId) => stages.find((s) => (s.contacts || []).some((c) => c.id === contactId)) || null;

// The server's { stages, unassigned }: unassigned cards get their own lane at the front, shown
// only when there are any (they were silently merged into the first stage before).
export function lanesFrom(data) {
  const stages = (data?.stages || []).map((s) => ({ ...s, contacts: s.contacts || [] }));
  const un = data?.unassigned || [];
  return un.length ? [{ id: UNASSIGNED_ID, name: 'Unassigned', unassigned: true, contacts: un }, ...stages] : stages;
}
